import api from '@/services/api';
import { toMinorUnits } from '@features/quote/utils/money';
import type { AddOn } from '../types/addon.types';

const smrPath = '/smr';

// The SMR endpoint returns either a plain array or a paginated { data: [] } envelope.
// Normalize both shapes.
interface Paginated<T> {
    data: T[];
    [k: string]: unknown;
}

const normalize = <T>(payload: T[] | Paginated<T>): T[] => {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray((payload as Paginated<T>).data)) {
        return (payload as Paginated<T>).data;
    }
    return [];
};

interface RawApiAddOnPivot {
    auto_include?: boolean;
    mandatory?: boolean;
    auto_include_link?: string | null;
}

interface RawApiAddOn {
    id: string;
    name: string;
    description?: string;
    category?: string | null;
    price?: number | string | null;
    gross_price?: number | string | null;
    price_gross?: number | string | null;
    effective_price?: number | string | null;
    effective_gross_price?: number | string | null;
    vat_rate_percent?: string | null;
    retired_at?: string | null;
    pivot?: RawApiAddOnPivot;
    [key: string]: unknown;
}

// `effective_*` arrive 4dp (a `decimal:0,4` override), but quote-service validates
// `add_ons.*.price`/`gross_price` as `decimal:0,2` — an unrounded line would 422 on save.
const roundToPence = (value: number): number => toMinorUnits(value) / 100;

const transformApiAddOn = (raw: RawApiAddOn): AddOn => {
    return {
        id: raw.id,
        name: raw.name,
        description: raw.description ?? '',
        category: raw.category ?? undefined,
        price: roundToPence(Number(raw.effective_price ?? raw.price ?? 0)),
        gross_price: roundToPence(
            Number(raw.effective_gross_price ?? raw.gross_price ?? raw.price_gross ?? 0)
        ),
        vat_rate_percent: raw.vat_rate_percent ?? undefined,
        auto_include: raw.pivot?.auto_include === true,
        mandatory: raw.pivot?.mandatory === true,
        auto_include_link: raw.pivot?.auto_include_link ?? null,
    };
};

export const fetchAddOnsByProduct = async (productId: string): Promise<AddOn[]> => {
    if (!productId) return [];
    const url = `${smrPath}/products/${productId}/addons`;
    const res = await api.get<RawApiAddOn[] | Paginated<RawApiAddOn>>(url);
    const normalized = normalize(res.data);
    return normalized.map(transformApiAddOn);
};
